import React from 'react';
import { BookOpen, Music, Mic2, CheckCircle2 } from 'lucide-react';
import FadeIn from './FadeIn';

const CourseCard: React.FC<{
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  features: string[];
  delay: number;
}> = ({ title, subtitle, icon, features, delay }) => (
  <FadeIn delay={delay} className="group relative bg-white p-8 rounded-xl border border-gray-100 hover:border-royal-maroon/30 transition-all duration-300 hover:shadow-2xl hover:shadow-royal-maroon/5 hover:-translate-y-2">
    <div className="absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity">
        <Music size={100} className="text-royal-maroon rotate-12" />
    </div>
    
    <div className="w-14 h-14 bg-sand/50 rounded-full flex items-center justify-center text-royal-maroon mb-6 group-hover:bg-royal-maroon group-hover:text-white transition-colors duration-300">
      {icon}
    </div>
    
    <h3 className="font-display text-2xl text-gray-900 mb-2">{title}</h3>
    <p className="text-sm text-royal-maroon font-medium uppercase tracking-wider mb-6">{subtitle}</p>
    
    <ul className="space-y-3 mb-8">
      {features.map((feature, idx) => (
        <li key={idx} className="flex items-start gap-3 text-gray-600 font-light">
          <CheckCircle2 size={16} className="text-royal-maroon mt-1 shrink-0" />
          <span className="text-sm">{feature}</span>
        </li>
      ))}
    </ul>
    
    <button className="w-full py-3 border border-gray-200 text-gray-600 text-sm rounded hover:bg-royal-maroon hover:text-white hover:border-royal-maroon transition-all duration-300">
      View Syllabus
    </button>
  </FadeIn>
);

const Courses: React.FC = () => {
  return (
    <section id="curriculum" className="py-24 bg-ivory relative">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-20">
            <FadeIn>
                <h2 className="font-display text-4xl md:text-5xl text-gray-900 mb-6">Our Curriculum</h2>
                <div className="w-24 h-1 bg-royal-maroon mx-auto mb-8"></div>
                <p className="text-gray-600 font-light text-lg">
                    A structured journey from the fundamental swaras to complex Manodharma, designed to mold complete musicians.
                </p>
            </FadeIn>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          <CourseCard
            title="Aarambha"
            subtitle="Beginner Level"
            icon={<BookOpen size={24} />}
            delay={0}
            features={[
              "Introduction to Sruti and Laya",
              "Sarali, Janta, and Dhatu Varisais",
              "Alankaras in different Talas",
              "Geethams and Swarajathis",
              "Basic theory of Carnatic Music"
            ]}
          />
          <CourseCard
            title="Madhyama"
            subtitle="Intermediate Level"
            icon={<Music size={24} />}
            delay={200}
            features={[
              "Adi Tala Varnams",
              "Introduction to Gamakas",
              "Kritis by the Trinity",
              "Understanding Raga Lakshanas",
              "Devotional songs & Bhajans"
            ]}
          />
          <CourseCard
            title="Nipuna"
            subtitle="Advanced Level"
            icon={<Mic2 size={24} />}
            delay={400}
            features={[
              "Complex Ata Tala Varnams",
              "Manodharma Sangeetham",
              "Ragam Tanam Pallavi (RTP)",
              "Kalpana Swaras & Neraval",
              "Concert presentation skills"
            ]}
          />
        </div>
      </div>
    </section>
  );
};

export default Courses;